import { forwardRef } from "react";
import { Repeat, Clock3, Users, Pencil } from "lucide-react";
import { cn } from "@/lib/utils";
import { colourVar, dragStore, timeLabel, WHOLE } from "./engine";

/**
 * One calendar item drawn as a bar/chip. Entries are draggable (HTML5 DnD), pending bookings and
 * recurring practices are not. `--c` carries the room hue so the CSS utilities can tint it.
 */
export const CalBar = forwardRef(({ item, colours, before = false, after = false, dense = false, ghost = false, showTime = true, className, onOpen, style, testId }, ref) => {
  const draggable = item.kind === "entry" && !ghost;
  const kindClass = ghost ? "cal-ghost" : item.kind === "pending" ? "cal-pending" : item.kind === "recurring" ? "cal-recurring" : "cal-bar";
  const whole = item.room_ids?.includes(WHOLE);
  const time = showTime ? timeLabel(item) : "";
  return (
    <button
      ref={ref}
      type="button"
      draggable={draggable}
      onDragStart={draggable ? (e) => { e.dataTransfer.setData("text/plain", item.id); e.dataTransfer.setData("text/x-offset", String(e.nativeEvent?.offsetX ?? 0)); e.dataTransfer.effectAllowed = "move"; dragStore.current = { item, offset: e.nativeEvent?.offsetX ?? 0 }; e.currentTarget.classList.add("cal-dragging"); } : undefined}
      onDragEnd={draggable ? (e) => { dragStore.current = null; e.currentTarget.classList.remove("cal-dragging"); } : undefined}
      onClick={(e) => { e.stopPropagation(); onOpen?.(item); }}
      className={cn(
        "group/bar flex w-full min-w-0 items-center gap-1.5 overflow-hidden rounded-[6px] text-left leading-none transition-[background-color,box-shadow,transform] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-1 active:scale-[0.99]",
        dense ? "h-[22px] px-1.5 text-[11.5px]" : "min-h-[26px] px-2 py-1 text-[12.5px]",
        kindClass,
        before && "cal-bar-cont-l",
        after && "cal-bar-cont-r",
        draggable && "cursor-grab active:cursor-grabbing",
        className,
      )}
      style={{ "--c": colourVar(item.room_ids, colours), ...style }}
      title={`${item.title}${time ? ` · ${time}` : ""}${item.room_names?.length ? ` · ${item.room_names.join(", ")}` : ""}`}
      data-testid={testId || `cal-item-${item.kind}-${item.id}`}
      data-kind={item.kind}
      data-date={item.start_date}
      data-occurrence={item.occurrence_date || undefined}
    >
      {item.kind === "recurring" && <Repeat className="h-3 w-3 shrink-0 opacity-70" aria-hidden="true" />}
      {item.kind === "recurring" && item.overridden && <Pencil className="h-2.5 w-2.5 shrink-0 opacity-70" aria-label="Endra for denne datoen" />}
      {item.kind === "pending" && <Clock3 className="h-3 w-3 shrink-0 text-status-warning" aria-hidden="true" />}
      {whole && item.kind === "entry" && !ghost && <Users className="h-3 w-3 shrink-0 opacity-70" aria-hidden="true" />}
      {time && !before && <span className="shrink-0 tabular text-muted-foreground">{time}</span>}
      <span className={cn("min-w-0 flex-1 truncate", item.kind === "entry" ? "font-medium" : "font-normal")}>{item.kind === "pending" ? `Ventar · ${item.title}` : item.title}</span>
    </button>
  );
});
CalBar.displayName = "CalBar";

/** Tiny coloured dot for the compact (mobile) month grid. */
export const CalDot = ({ item, colours }) => (
  <span
    className={cn("inline-block h-1.5 w-1.5 shrink-0 rounded-full", item.kind === "pending" && "bg-transparent", item.kind === "recurring" && "opacity-50")}
    style={{ background: item.kind === "pending" ? "transparent" : `hsl(${colourVar(item.room_ids, colours)})`, boxShadow: item.kind === "pending" ? "inset 0 0 0 1px hsl(var(--status-pending))" : undefined }}
    aria-hidden="true"
  />
);

/** Room legend chip – doubles as the room filter. */
export const RoomChip = ({ room, colourIndex, active, dimmed, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={cn(
      "inline-flex h-8 shrink-0 items-center gap-2 whitespace-nowrap rounded-full border px-2.5 text-[12.5px] font-medium transition-[background-color,border-color,opacity] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2",
      active ? "border-foreground bg-foreground text-background" : "border-border bg-card text-foreground hover:border-foreground/40",
      dimmed && !active && "opacity-50",
    )}
    data-testid={testId}
  >
    <span className="h-2.5 w-2.5 rounded-full" style={{ background: `hsl(var(--room-${colourIndex}))` }} aria-hidden="true" />
    {room.short_name || room.name}
  </button>
);
